import { Button, Stack, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import ModalV2 from '/@/components/bayer/modal-v2';
import SelectionListItem from '/@/components/bayer/selection-list-item';
import Icon from '/@/components/icon';
import settings from '/@/core/settings';
import { useOwnUserNode } from '/@/hooks/nodes';
import { useIsValidatedBayerUserType } from '/@/hooks/session';
import { _ } from '/@/locale';
import { getAppInstance, getCornRecommendationEnabled, getSoyRecommendationEnabled } from '/@/store/session/selectors';
import { mpTrackEvent } from '/@/store/tracking/mixpanel-events';
import type { Field } from '/@/types/field';
import { isPoint } from '/@/utils';
import { reverse } from '/@/utils/router';
import { managementRecommendationUrl, seedRecommendationUrl } from '/@/views/recommendations/seed-recommendation-route';

export interface RecommendationOption {
    icon: string;
    title: string;
    description?: string;
    rightItem?: 'radio' | 'checkbox';
    isSelected: (itemIndex: number) => boolean;
    onClick: (itemIndex: number) => void;
    onAcceptClick?: () => void;
    hidden?: boolean;
    disabled?: boolean;
    disabledTooltip?: string;
    dataTestId?: string;
}

function CreateRecommendationModal({
    open,
    setOpen,
    selectedField = undefined,
    setSelectedField = () => {},
    userNodeId,
    entrySource,
}: {
    open: boolean;
    setOpen: React.Dispatch<React.SetStateAction<boolean>>;
    selectedField?: Field;
    setSelectedField?: (value: Field | undefined) => void;
    userNodeId?: number;
    entrySource:
        | 'product-exploration'
        | 'app-navbar'
        | 'field-list'
        | 'recommendation-list'
        | 'growers-list'
        | 'rv-home'
        | 'other-home'
        | 'field-monitoring';
}) {
    const navigate = useNavigate();
    const [selectedItem, setSelectedItem] = useState<number | undefined>(undefined);
    const isItemSelected = (itemIndex: number) => selectedItem === itemIndex;
    const appInstance = useSelector(getAppInstance);
    const cornRecommendationEnabled = useSelector((state) => getCornRecommendationEnabled(state, appInstance));
    const soyRecommendationEnabled = useSelector((state) => getSoyRecommendationEnabled(state, appInstance));
    const cropProtectionEnabled = useSelector(getAppInstance).enableCropProtectionRecommendation;
    const nitrogenRecommendationEnabled = useSelector(getAppInstance).enableNitogrenRecommendation;

    // N-Smart is meant for growers' fields. For Bayer users it is disabled when the field
    // (or the scoped node) belongs to their own node.
    const isBayer = useIsValidatedBayerUserType();
    const ownNode = useOwnUserNode();
    const ownNodeId = ownNode.data?.id;
    const isOwnNodeNSmart =
        isBayer && ownNodeId !== undefined && (selectedField?.userNode === ownNodeId || userNodeId === ownNodeId);

    useEffect(() => {
        open &&
            mpTrackEvent('Generate Recommendation flow:Started', {
                entry_source: entrySource,
            });
    }, [entrySource, open]);

    const recommendationOptions: RecommendationOption[] = [
        {
            icon: 'seedBag',
            title: _('Conocer el híbrido más adecuado para un lote'),
            dataTestId: 'recommendation-type-corn-option',
            description: _('Te recomendamos el híbrido que mejor se adapta al ambiente y al rendimiento objetivo.'),
            rightItem: 'radio',
            isSelected: (itemIndex: number) => isItemSelected(itemIndex),
            onClick: (itemIndex: number) => {
                if (isItemSelected(itemIndex)) {
                    setSelectedItem(undefined);
                } else {
                    setSelectedItem(itemIndex);
                    mpTrackEvent('Recommendation Type Selected', {
                        recommendation_type: 'corn',
                        entry_source: entrySource,
                    });
                }
            },
            onAcceptClick: () => {
                // A preselected field skips the wizard's field-selection step.
                navigate(
                    selectedField
                        ? seedRecommendationUrl({ crop: 'corn', fieldId: selectedField.id, entrySource })
                        : seedRecommendationUrl({ crop: 'corn', entrySource: 'rv-home-modal', userNodeId })
                );
            },
            hidden: !cornRecommendationEnabled,
        },
        {
            icon: 'cornDensityRecommendation',
            title: _('Recomendación de manejo'),
            description: _('Obtené la densidad, la fenología y la fertilización recomendadas para un híbrido.'),
            rightItem: 'radio',
            isSelected: (itemIndex: number) => isItemSelected(itemIndex),
            onClick: (itemIndex: number) => {
                if (isItemSelected(itemIndex)) {
                    setSelectedItem(undefined);
                } else {
                    setSelectedItem(itemIndex);
                    mpTrackEvent('Recommendation Type Selected', {
                        recommendation_type: 'density',
                        entry_source: entrySource,
                    });
                }
            },
            onAcceptClick: () => {
                navigate(
                    managementRecommendationUrl({
                        fieldId: selectedField?.id,
                        entrySource,
                        userNodeId,
                    })
                );
            },
            // The management flow runs on the same corn hybrid data as the recommendation above
            // it, so it needs the same published-corn-brand check rather than a looser one.
            hidden: !cornRecommendationEnabled,
        },
        {
            icon: 'soy2',
            title: _('Variedad de Soja'),
            description: _('Te recomendamos la variedad que mejor se adapta al ambiente y al rendimiento objetivo.'),
            rightItem: 'radio',
            isSelected: (itemIndex: number) => isItemSelected(itemIndex),
            onClick: (itemIndex: number) => {
                if (isItemSelected(itemIndex)) {
                    setSelectedItem(undefined);
                } else {
                    setSelectedItem(itemIndex);
                    mpTrackEvent('Recommendation Type Selected', {
                        recommendation_type: 'soy',
                        entry_source: entrySource,
                    });
                }
            },
            onAcceptClick: () => {
                navigate(
                    selectedField
                        ? seedRecommendationUrl({ crop: 'soy', fieldId: selectedField.id, entrySource })
                        : seedRecommendationUrl({ crop: 'soy', entrySource: 'rv-home-modal', userNodeId })
                );
            },
            hidden: !soyRecommendationEnabled,
        },
        {
            icon: 'diseaseRecommendation',
            title: _('Proteger el cultivo con fitosanitarios'),
            description: _('Sugerimos el producto indicado para el manejo de plagas, enfermedades y malezas.'),
            rightItem: 'radio',
            isSelected: (itemIndex: number) => isItemSelected(itemIndex),
            onClick: (itemIndex: number) => {
                if (isItemSelected(itemIndex)) {
                    setSelectedItem(undefined);
                } else {
                    setSelectedItem(itemIndex);
                    mpTrackEvent('Recommendation Type Selected', {
                        recommendation_type: 'crop_protection',
                        entry_source: entrySource,
                    });
                }
            },
            onAcceptClick: () => {
                mpTrackEvent('Crop Protection Recommendation began', {
                    entry_source: 'rv-home-modal',
                });
                navigate(
                    reverse('cropProtectionRecommendation', {
                        qs: { fieldId: selectedField?.id, userNodeId },
                    })
                );
            },
            hidden: !cropProtectionEnabled,
        },
        {
            icon: 'cropProyection',
            title: _('Planificar la estrategia de refertilización'),
            description: _('Con NSmart obtenés una dosis de nitrógeno ajustada al ambiente y al potencial del lote.'),
            rightItem: 'radio',
            isSelected: (itemIndex: number) => isItemSelected(itemIndex),
            onClick: (itemIndex: number) => {
                if (isItemSelected(itemIndex)) {
                    setSelectedItem(undefined);
                } else {
                    setSelectedItem(itemIndex);
                    mpTrackEvent('Recommendation Type Selected', {
                        recommendation_type: 'nitrogen',
                        entry_source: entrySource,
                    });
                }
            },
            onAcceptClick: () => {
                mpTrackEvent('N-Smart Recommendation began', {
                    entry_source: 'rv-home-modal',
                });
                if (selectedField) {
                    navigate(
                        reverse('nitrogenRecommendation.NitrogenRecommendationCreate', {
                            params: { fieldId: selectedField?.id, userNodeId },
                        })
                    );
                } else {
                    navigate(reverse('nitrogenRecommendation.NitrogenRecommendationList'));
                }
            },
            hidden: !nitrogenRecommendationEnabled,
            disabled: isPoint(selectedField?.geometry) || isOwnNodeNSmart,
            disabledTooltip: isOwnNodeNSmart
                ? _('N-Smart solo está disponible para los lotes de tus productores.')
                : _('No disponible para campos creados como un punto'),
        },
    ];

    const enabledRecommendations = recommendationOptions.filter((option) => !option.hidden);

    return (
        <ModalV2
            title={_('Qué querés recomendar?')}
            open={open}
            onClose={() => {
                setSelectedField(undefined);
                setSelectedItem(undefined);
                setOpen(false);
            }}
            bodyProps={{ alignItems: 'stretch', sx: { textAlign: 'left' } }}
            data-testid='recommendation-type-modal'
            actionsProps={{ sx: { justifyContent: 'flex-end' } }}
            actions={
                <Button
                    variant='contained'
                    color='secondary'
                    data-testid='recommendation-type-modal-accept-button'
                    disabled={selectedItem === undefined}
                    onClick={() => {
                        if (selectedItem !== undefined) {
                            enabledRecommendations[selectedItem]?.onAcceptClick?.();
                            setOpen(false);
                        }
                    }}
                >
                    {_('Aceptar')}
                </Button>
            }
        >
            <Stack spacing={2} width='100%' p={1} maxWidth={550} sx={{ mx: 4 }}>
                {enabledRecommendations.length === 0 ? (
                    <Stack spacing={1} textAlign='center'>
                        <Typography variant='h6' color='primary'>
                            {_('No hay opciones disponibles por el momento')}
                        </Typography>
                        <Typography variant='body2' color='primary'>
                            {_('Próximamente se agregarán opciones')}
                        </Typography>
                    </Stack>
                ) : (
                    enabledRecommendations.map((option, index) => (
                        <SelectionListItem
                            key={`${option.icon}-${option.title}-${index}`}
                            dataTestId={option.dataTestId}
                            leftItem={
                                <Icon
                                    theme={settings.app.theme}
                                    type={option.icon}
                                    variant={option.isSelected(index) ? 'primary' : 'accent'}
                                />
                            }
                            title={option.title}
                            subtitle={option.description}
                            rightItem={option.rightItem}
                            isSelected={option.isSelected(index)}
                            onClick={() => option.onClick(index)}
                            disabled={option?.disabled || !option?.onAcceptClick}
                            disabledTooltip={option?.disabledTooltip}
                        />
                    ))
                )}
            </Stack>
        </ModalV2>
    );
}

export default CreateRecommendationModal;
